import countdownAudioUrl from '@/static/audio/321go.mp3';
import diamondAudioUrl from '@/static/audio/diamond.mp3';
import honkAudioUrl from '@/static/audio/honk.mp3';
import lastSecondsAudioUrl from '@/static/audio/last-seconds.mp3';
import playAgainAudioUrl from '@/static/audio/play-again.mp3';
import timeAudioUrl from '@/static/audio/time.mp3';
import winAudioUrl from '@/static/audio/win.mp3';
import { useStorageState } from '@lemonade-hq/cdk';
import { useCallback, useEffect, useRef } from 'react';

export interface UseGameAudio {
  honkAudio: HTMLAudioElement;
  diamondAudio: HTMLAudioElement;
  timeAudio: HTMLAudioElement;
  countdownAudio: HTMLAudioElement;
  winAudio: HTMLAudioElement;
  lastSecondsAudio: HTMLAudioElement;
  playAgainAudio: HTMLAudioElement;
  isMuted: boolean;
  playAudio: (audio: HTMLAudioElement) => void;
  stopActiveAudios: () => void;
  toggleMuted: () => void;
}

export const useGameAudio = (): UseGameAudio => {
  const { storedValue: isMuted, setValue } = useStorageState<boolean>('car_lp_game_mute', false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const toggleMutedExternal = useCallback(() => {
    setValue(!isMuted);
  }, [isMuted]);

  const honkAudio = useRef<HTMLAudioElement>();
  const diamondAudio = useRef<HTMLAudioElement>();
  const timeAudio = useRef<HTMLAudioElement>();
  const countdownAudio = useRef<HTMLAudioElement>();
  const winAudio = useRef<HTMLAudioElement>();
  const lastSecondsAudio = useRef<HTMLAudioElement>();
  const playAgainAudio = useRef<HTMLAudioElement>();

  const activeAudios = useRef<HTMLAudioElement[]>([]);

  useEffect(() => {
    honkAudio.current = new Audio(honkAudioUrl);
    diamondAudio.current = new Audio(diamondAudioUrl);
    timeAudio.current = new Audio(timeAudioUrl);
    countdownAudio.current = new Audio(countdownAudioUrl);
    winAudio.current = new Audio(winAudioUrl);
    lastSecondsAudio.current = new Audio(lastSecondsAudioUrl);
    playAgainAudio.current = new Audio(playAgainAudioUrl);
  }, []);

  const toggleMute = useCallback((muted: boolean) => {
    [
      diamondAudio.current,
      timeAudio.current,
      countdownAudio.current,
      winAudio.current,
      lastSecondsAudio.current,
      playAgainAudio.current,
      ...activeAudios.current,
    ].forEach(audio => {
      // eslint-disable-next-line no-param-reassign
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      audio!.muted = muted;
    });
  }, []);

  const stopActiveAudios = useCallback(() => {
    activeAudios.current.forEach(audio => {
      audio.pause();
    });
  }, []);

  useEffect(() => {
    toggleMute(!!isMuted);
  }, [toggleMute, isMuted]);

  const playAudio = useCallback((audio: HTMLAudioElement) => {
    const clone = audio.cloneNode(true) as HTMLAudioElement;
    clone.muted = audio.muted;
    activeAudios.current.push(clone);

    clone.play();

    clone.addEventListener(
      'ended',
      () => {
        activeAudios.current.splice(activeAudios.current.indexOf(clone), 1);
      },
      { once: true },
    );
  }, []);

  return {
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    honkAudio: honkAudio.current!,
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    diamondAudio: diamondAudio.current!,
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    timeAudio: timeAudio.current!,
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    countdownAudio: countdownAudio.current!,
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    winAudio: winAudio.current!,
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    lastSecondsAudio: lastSecondsAudio.current!,
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    playAgainAudio: playAgainAudio.current!,
    isMuted: !!isMuted,
    toggleMuted: toggleMutedExternal,
    playAudio,
    stopActiveAudios,
  };
};
